// src/pages/Login.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import {
  Box,
  TextField,
  Button,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAppVersion } from "../utils/version";

const PASSWORD_MIN_LENGTH = 12;
const UPPERCASE_RE = /[A-Z]/;
const LOWERCASE_RE = /[a-z]/;
const NUMBER_RE = /[0-9]/;

type PasswordChecks = {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
};

function getPasswordChecks(value: string): PasswordChecks {
  return {
    minLength: value.length >= PASSWORD_MIN_LENGTH,
    uppercase: UPPERCASE_RE.test(value),
    lowercase: LOWERCASE_RE.test(value),
    number: NUMBER_RE.test(value),
  };
}

function isPasswordValid(value: string): boolean {
  const checks = getPasswordChecks(value);
  return Object.values(checks).every(Boolean);
}

function getPasswordIssues(checks: PasswordChecks): string[] {
  const issues: string[] = [];
  if (!checks.minLength) issues.push(`at least ${PASSWORD_MIN_LENGTH} characters`);
  if (!checks.uppercase) issues.push("an uppercase letter");
  if (!checks.lowercase) issues.push("a lowercase letter");
  if (!checks.number) issues.push("a number");
  return issues;
}

// Ensure user profile exists (+ backfill accountRole:user)
async function ensureUserDoc(user: User) {
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);

  const base = {
    uid: user.uid,
    email: user.email ?? "",
    emailLower: (user.email ?? "").toLowerCase(),
    displayName: user.displayName ?? "",
    nameLower: (user.displayName ?? "").toLowerCase(),
    firstName: "",
    lastName: "",
    lastLoginAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (!snap.exists()) {
    await setDoc(ref, { ...base, accountRole: "user", createdAt: serverTimestamp() }, { merge: true });
  } else if (!(snap.data() as any)?.accountRole) {
    await setDoc(ref, { accountRole: "user", lastLoginAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  } else {
    await setDoc(ref, { lastLoginAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  }
}

// Tabs helpers
type TabPanelProps = { children?: React.ReactNode; index: number; value: number };
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`tab-panel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}
const a11yProps = (i: number) => ({ id: `tab-${i}`, "aria-controls": `tab-panel-${i}` });

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0); // 0 = Log In, 1 = Sign Up
  const [passwordTouched, setPasswordTouched] = useState(false);

  const passwordChecks = getPasswordChecks(password);
  const passwordIsValid = isPasswordValid(password);
  const confirmMatches = confirmPassword.length > 0 && confirmPassword === password;
  const showPasswordIssue = passwordTouched && password.length > 0 && !passwordIsValid;
  const passwordIssues = getPasswordIssues(passwordChecks);

  // Navigate when signed in
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(false); // stop spinner either way

      if (user && !user.isAnonymous) {
        try {
          await ensureUserDoc(user);
          navigate("/surveys", { replace: true });
        } catch (e) {
          console.error("ensureUserDoc failed:", e);
        }
      }
    });

    return () => unsub();
  }, [navigate]);
  

  const handleTabChange = (_e: React.SyntheticEvent, v: number) => {
    setTabValue(v);
    setError(null);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setPasswordTouched(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      await ensureUserDoc(cred.user);
    } catch (err: any) {
      console.error("Login Error:", err);
      const msg =
        err?.code === "auth/operation-not-allowed"
          ? "Email/password sign-in is disabled. Enable it in Firebase → Authentication → Sign-in method."
          : err?.message || "Login failed. Please check your credentials.";
      setError(msg);
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`);
      setLoading(false);
      return;
    }
    if (!passwordIsValid) {
      setError(
        `Password must be at least ${PASSWORD_MIN_LENGTH} characters and include uppercase, lowercase, and number.`
      );
      setLoading(false);
      return;
    }

    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await ensureUserDoc(cred.user);
    } catch (err: any) {
      console.error("Sign Up Error:", err);
      const msg =
        err?.code === "auth/operation-not-allowed"
          ? "Email/password sign-up is disabled. Enable it in Firebase → Authentication → Sign-in method."
          : err?.message || "Sign up failed. Please try again.";
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        p: 3,
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          p: 4,
          borderRadius: 2,
          boxShadow: 3,
          width: "100%",
          maxWidth: 420,
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Welcome to StakeholderNet
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="login/signup tabs" centered>
            <Tab label="Log In" {...a11yProps(0)} />
            <Tab label="Sign Up" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Log In */}
            <TabPanel value={tabValue} index={0}>
              <form onSubmit={handleLogin} autoComplete="on" name="login">
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username" // helps managers
                />
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, mb: 1 }}>
                  Log In
                </Button>
                <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                  <Link to="/forgot">Forgot password?</Link>
                </Typography>
              </form>
            </TabPanel>

            {/* Sign Up */}
            <TabPanel value={tabValue} index={1}>
              {/* Important: make the form clearly a registration form for password generators */}
              <form onSubmit={handleSignUp} autoComplete="on" name="signup">
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  fullWidth
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
                <TextField
                  label="Password"
                  name="new-password"
                  type="password"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordTouched(true);
                  }}
                  onBlur={() => setPasswordTouched(true)}
                  required
                  autoComplete="new-password" // triggers strong password suggestion
                  helperText={`Use at least ${PASSWORD_MIN_LENGTH} characters with uppercase, lowercase, and number.`}
                />
                {showPasswordIssue && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                    Add {passwordIssues.join(", ")}.
                  </Typography>
                )}
                <TextField
                  label="Confirm Password"
                  name="confirm-password"
                  type="password"
                  fullWidth
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  error={confirmPassword.length > 0 && !confirmMatches}
                  helperText={
                    confirmPassword.length === 0
                      ? "Re-enter the password to confirm it."
                      : confirmMatches
                      ? "Passwords match."
                      : "Passwords do not match."
                  }
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2, mb: 1 }}
                  disabled={!passwordIsValid || (confirmPassword.length > 0 && !confirmMatches)}
                >
                  Sign Up
                </Button>
              </form>
            </TabPanel>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 2 }}>
              {getAppVersion()}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
